import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SessionGroupStats, SessionStabilityStats } from '../../api/ocpp-session-analysis-api.service';
import { analysisDelta, DeltaDirection, DeltaView, formatDuration, formatPercent } from './session-analysis-format';

@Component({
  selector: 'app-session-group-table',
  standalone: true,
  imports: [CommonModule, MatSortModule, MatTooltipModule],
  templateUrl: './session-group-table.component.html',
  styleUrls: ['./session-group-table.component.scss']
})
export class SessionGroupTableComponent implements OnChanges {
  @Input({ required: true }) groups: SessionGroupStats[] = [];
  @Input() baselineGroups: SessionGroupStats[] | null = null;
  @Input() showHardware = false;

  sorted: SessionGroupStats[] = [];
  private sortActive = 'shortRate';
  private sortDirection: Sort['direction'] = 'desc';

  formatPercent = formatPercent;
  formatDuration = formatDuration;

  readonly columnTooltips = {
    chargePoints:
      'Nombre de bornes du groupe avec au moins une session dont le début est dans la fenêtre. Une session commencée avant la fenêtre n’est pas comptée.',
    sessions:
      'Nombre de sessions dont le début est dans la fenêtre. Elles sont triées par date de début. Si la date de fin manque, l’analyse utilise la date de début de la session suivante, sans l’enregistrer. S’il n’y a pas de session suivante, la session reste ouverte. Les sessions sans date de début sont ignorées.',
    veryShort:
      'Part des sessions fermées dont la durée (fin − début) est inférieure à 10 secondes. Une fin manquante est d’abord remplacée par le début de la session suivante. Le dénominateur est le nombre de sessions fermées. Ces sessions sont aussi comptées dans « < 60 s ».',
    short:
      'Part des sessions fermées dont la durée est inférieure à 60 secondes, y compris celles de moins de 10 secondes. Une fin manquante est d’abord remplacée par le début de la session suivante. Le dénominateur est le nombre de sessions fermées.',
    bursts:
      'Part des sessions fermées qui redémarrent dans les 30 secondes après la fermeture de la session fermée précédente, sur la même borne. Un chevauchement (écart négatif) n’est pas une rafale. Le dénominateur est le nombre de sessions fermées.',
    uptime:
      'Moyenne, sur les bornes du groupe, du temps connecté divisé par la durée de la fenêtre. Seules les sessions commencées pendant la fenêtre entrent dans le calcul. Le temps après la fin de la fenêtre n’est pas compté, et chaque borne est plafonnée à 100 %. Une session dont la fin a été déduite du début de la suivante est traitée comme fermée. Une session toujours sans fin est comptée jusqu’à maintenant, sans dépasser la fin de la fenêtre.',
    median:
      'Durée médiane des sessions fermées, y compris celles dont la fin manquante a été remplacée par le début de la session suivante. Les durées sont rangées dans des tranches (1 s, 10 s, 30 s, 1 min, 5 min, 30 min, 4 h, 24 h) et la valeur affichée est le milieu de la tranche qui dépasse 50 % des sessions. Pour un groupe, les tranches de toutes les bornes sont additionnées : ce n’est pas la moyenne des médianes.',
    unstable:
      'Bornes qui déclenchent au moins une règle. Sessions < 10 s : alerte dès 5 sessions et 5 %, danger dès 10 et 20 %. Sessions < 60 s : alerte dès 10 sessions fermées et 25 %, danger dès 20 et 50 %. Rafales : alerte dès 5 et 25 %, danger dès 10 et 50 %. Chevauchements : alerte dès 3, danger dès 10. Présence : alerte sous 80 %, danger sous 50 %, seulement si la fenêtre dure au moins 6 h et la borne a au moins 3 sessions.'
  };

  ngOnChanges(): void {
    this.sorted = this.sortGroups();
  }

  onSort(sort: Sort): void {
    this.sortActive = sort.active;
    this.sortDirection = sort.direction;
    this.sorted = this.sortGroups();
  }

  get groupTooltip(): string {
    switch (this.groups[0]?.scope) {
      case 'CHARGE_POINT':
        return 'Identifiant de la borne (namespace/id). La ligne du dessous rappelle le vendeur, le modèle et le firmware enregistrés. « Inconnu » si la borne ne les a pas encore envoyés.';
      case 'NAMESPACE':
        return 'Organisation, lue dans le namespace de l’identifiant de borne (la partie avant le « / »).';
      case 'VENDOR':
        return 'Vendeur enregistré sur la borne. « Inconnu » si ce champ est vide. La casse est ignorée pour regrouper.';
      case 'MODEL':
        return 'Couple vendeur et modèle enregistrés sur la borne. « Inconnu » si un des deux champs est vide. La casse est ignorée pour regrouper.';
      case 'FIRMWARE':
        return 'Triplet vendeur, modèle et firmware enregistrés sur la borne. « Inconnu » si un champ est vide. La casse est ignorée pour regrouper.';
      default:
        return 'Regroupement des bornes qui ont eu au moins une session sur la fenêtre.';
    }
  }

  baseline(key: string): SessionStabilityStats | undefined {
    return this.baselineGroups?.find(group => group.key === key)?.stats;
  }

  cellDelta(
    key: string,
    current: number,
    field: keyof SessionStabilityStats,
    direction: DeltaDirection
  ): DeltaView | null {
    if (!this.baselineGroups) {
      return null;
    }
    const stats = this.baseline(key);
    const baseline = stats ? Number(stats[field]) : 0;
    return analysisDelta(current, baseline, direction);
  }

  private sortGroups(): SessionGroupStats[] {
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    return [...this.groups].sort((left, right) => {
      const compared = this.compare(left, right) * direction;
      return compared === 0 ? left.label.localeCompare(right.label, 'fr') : compared;
    });
  }

  private compare(left: SessionGroupStats, right: SessionGroupStats): number {
    if (this.sortActive === 'label') {
      return left.label.localeCompare(right.label, 'fr');
    }
    return this.numericValue(left) - this.numericValue(right);
  }

  private numericValue(group: SessionGroupStats): number {
    switch (this.sortActive) {
      case 'chargePointCount':
        return group.stats.chargePointCount;
      case 'sessionCount':
        return group.stats.sessionCount;
      case 'veryShortRate':
        return group.stats.veryShortRate;
      case 'shortRate':
        return group.stats.shortRate;
      case 'burstRate':
        return group.stats.burstRate;
      case 'uptimeRatio':
        return group.stats.uptimeRatio;
      case 'medianDurationSeconds':
        return group.stats.medianDurationSeconds;
      case 'unstableChargePointCount':
        return group.stats.unstableChargePointCount;
      default:
        return 0;
    }
  }
}
